// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/hooks/usePipeline.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { pipelineService, type FiltrosVisaoPipeline } from '../services/pipelineService';
import type {
  CriarEstagioRequest,
  AtualizarEstagioRequest,
  CriarOportunidadeRequest,
  AtualizarOportunidadeRequest,
  MoverEstagioRequest,
  FecharOportunidadeRequest,
} from '../types';

const KEY_VISAO    = ['crm', 'pipeline', 'visao'] as const;
const KEY_ESTAGIOS = ['crm', 'pipeline', 'estagios'] as const;
const KEY_OPORT    = ['crm', 'pipeline', 'oportunidades'] as const;

export const useEstagiosPipeline = () =>
  useQuery({
    queryKey: KEY_ESTAGIOS,
    queryFn:  () => pipelineService.listarEstagios(),
  });

export const useVisaoPipeline = (filtros: FiltrosVisaoPipeline = {}) =>
  useQuery({
    queryKey: [...KEY_VISAO, filtros],
    queryFn:  () => pipelineService.obterVisao(filtros),
  });

export const useOportunidades = (params: {
  pagina?: number;
  tamanhoPagina?: number;
  estagioId?: number;
  vendedorId?: number;
  somenteAbertas?: boolean;
} = {}) =>
  useQuery({
    queryKey: [...KEY_OPORT, params],
    queryFn:  () => pipelineService.listar(params),
  });

export const useOportunidade = (id?: number) =>
  useQuery({
    queryKey: [...KEY_OPORT, 'detalhe', id],
    queryFn:  () => pipelineService.getById(id!),
    enabled:  !!id,
  });

export const usePipelineMutations = () => {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['crm', 'pipeline'] });
    qc.invalidateQueries({ queryKey: ['crm', 'dashboard'] });
  };

  return {
    criarEstagio: useMutation({
      mutationFn: (req: CriarEstagioRequest) => pipelineService.criarEstagio(req),
      onSuccess:  () => { invalidate(); toast.success('Estágio criado'); },
      onError:    (e) => toast.error(getApiErrorMessage(e)),
    }),
    atualizarEstagio: useMutation({
      mutationFn: ({ id, data }: { id: number; data: AtualizarEstagioRequest }) =>
        pipelineService.atualizarEstagio(id, data),
      onSuccess:  () => { invalidate(); toast.success('Estágio atualizado'); },
      onError:    (e) => toast.error(getApiErrorMessage(e)),
    }),
    removerEstagio: useMutation({
      mutationFn: (id: number) => pipelineService.removerEstagio(id),
      onSuccess:  () => { invalidate(); toast.success('Estágio removido'); },
      onError:    (e) => toast.error(getApiErrorMessage(e)),
    }),

    criarOportunidade: useMutation({
      mutationFn: (req: CriarOportunidadeRequest) => pipelineService.criar(req),
      onSuccess:  () => { invalidate(); toast.success('Oportunidade criada'); },
      onError:    (e) => toast.error(getApiErrorMessage(e)),
    }),
    atualizarOportunidade: useMutation({
      mutationFn: ({ id, data }: { id: number; data: AtualizarOportunidadeRequest }) =>
        pipelineService.atualizar(id, data),
      onSuccess:  () => { invalidate(); toast.success('Oportunidade atualizada'); },
      onError:    (e) => toast.error(getApiErrorMessage(e)),
    }),
    removerOportunidade: useMutation({
      mutationFn: (id: number) => pipelineService.remover(id),
      onSuccess:  () => { invalidate(); toast.success('Oportunidade removida'); },
      onError:    (e) => toast.error(getApiErrorMessage(e)),
    }),
    moverOportunidade: useMutation({
      mutationFn: ({ id, data }: { id: number; data: MoverEstagioRequest }) =>
        pipelineService.mover(id, data),
      onSuccess: () => invalidate(),
      onError:   (e) => toast.error(getApiErrorMessage(e)),
    }),
    fecharOportunidade: useMutation({
      mutationFn: ({ id, data }: { id: number; data: FecharOportunidadeRequest }) =>
        pipelineService.fechar(id, data),
      onSuccess:  (_, vars) => {
        invalidate();
        toast.success(vars.data.ganho ? 'Oportunidade ganha 🎉' : 'Oportunidade encerrada');
      },
      onError:    (e) => toast.error(getApiErrorMessage(e)),
    }),
  };
};
